import { Anchor, Select, Text } from '@byecode/ui'
import type { Field } from '@lighthouse/core'
import type { RichTextEditorProps, SendEmailParagraph } from '@lighthouse/shared'
import {
    DATE_SYSTEM,
    EMAIL_EDIT_TYPE,
    getDateVariableOption,
    ListItem4ByecodeUi,
    PARAGRAPH_TYPE,
    parseJsonExpression,
    RichTextEditor
} from '@lighthouse/shared'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import FormSegmentedControl from '@/components/BlockSettings/Common/FormSegmentedControl'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import { useSendEmailContext } from '../context'
import * as SC from '../styles'

const SCxListItemBaseInfo = styled.div`
    gap: 0;
    padding: 0 16px;
`

const SCxHighLightText = styled.div`
    background-color: var(--color-gray-100);
    padding: 8px;
    line-height: 20px;
    font-size: var(--font-size-sm);
    margin: 4px 0;
    border-radius: 4px;
`

const SCxRequired = styled.span`
    color: var(--color-red-500);
`

const SCxContainer = styled.div<{ show?: boolean }>`
    display: ${({ show }) => (show ? 'block' : 'none')};
    margin: 8px 0;
    border-radius: 8px;
    border: 1px solid var(--color-gray-200);
    .ProseMirror {
        min-height: 120px;
    }
    .ProseMirror > p {
        word-break: break-all;
    }
`

const paragraphTypeOptions = [
    {
        label: '常规',
        value: PARAGRAPH_TYPE.NORMAL
    },
    {
        label: '多行列表',
        value: PARAGRAPH_TYPE.MULTIPLE
    }
]

interface EmailParagraphConfigureProps {
    disabled?: boolean
    style?: React.CSSProperties
    value: SendEmailParagraph
    onChange: (value: SendEmailParagraph) => void
}

const fieldFilter = (field: Field) => field.innerType !== 'RICH_TEXT' && field.innerType !== 'NULL'

export const EmailParagraphConfigure: React.FunctionComponent<EmailParagraphConfigureProps> = ({ disabled, style, value, onChange }) => {
    const { upstreamOptions, multiNodeOptions, editType = EMAIL_EDIT_TYPE.RICHTEXT } = useSendEmailContext()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const paragraphType = value.type

    const dataSourceList = useDataSourceList(appId, envId)

    const dateOption = useMemo(() => getDateVariableOption(DATE_SYSTEM), [])

    const userOption = useGetUserRecordOptions()

    const richtextConfig: RichTextEditorProps['config'] = useMemo(
        () => ({
            mode: 'all',
            variable: {
                upstreamOptions,
                userOption,
                dateOption,
                dataSources: dataSourceList
            }
        }),
        [dataSourceList, dateOption, upstreamOptions, userOption]
    )

    const htmlConfig: RichTextEditorProps['config'] = useMemo(
        () => ({
            variable: {
                upstreamOptions,
                userOption,
                dateOption,
                dataSources: dataSourceList
            }
        }),
        [dataSourceList, dateOption, upstreamOptions, userOption]
    )

    return (
        <SCxListItemBaseInfo style={style}>
            <FormSegmentedControl
                label="段落类型"
                disabled={disabled}
                data={paragraphTypeOptions}
                value={value.type}
                size="sm"
                onChange={type => onChange({ ...value, type: type as PARAGRAPH_TYPE })}
            />
            {paragraphType === PARAGRAPH_TYPE.MULTIPLE && (
                <>
                    <SCxHighLightText>
                        <Text whiteSpace="normal">
                            从上游节点中选一个输出为多条记录的节点，即可发送多行列表的段落，详见{' '}
                            <Anchor
                                target="_blank"
                                style={{ fontWeight: 600, color: 'var(--color-main)' }}
                                to="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/drqrgvg10iuf8o90?singleDoc#"
                            >
                                帮助文档
                            </Anchor>{' '}
                        </Text>
                    </SCxHighLightText>
                    <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                        <Text>
                            选择上游节点<SCxRequired>*</SCxRequired>
                        </Text>
                        <SC.RightFill>
                            <Select
                                style={{ width: 180 }}
                                disabled={disabled}
                                value={value.parentNodeId}
                                onChange={parentNodeId => onChange({ ...value, parentNodeId: parentNodeId })}
                                options={multiNodeOptions}
                            />
                        </SC.RightFill>
                    </ListItem4ByecodeUi>
                </>
            )}
            <SCxContainer show={editType === EMAIL_EDIT_TYPE.RICHTEXT}>
                <RichTextEditor
                    disabled={disabled}
                    config={richtextConfig}
                    value={value.richtext}
                    onChange={richtext => onChange({ ...value, richtext })}
                    onEditorBlur={({ editor: e }) => {
                        const html = e.getHTML()
                        onChange({ ...value, targetRichtext: html })
                    }}
                    styles={{
                        editorContent: {
                            backgroundColor: 'var(--color-gray-50)',
                            borderRadius: 6,
                            maxHeight: '600px',
                            overflowY: 'auto'
                        }
                    }}
                />
            </SCxContainer>
            <SCxContainer show={editType === EMAIL_EDIT_TYPE.HTML}>
                <RichTextEditor
                    disabled={disabled}
                    config={htmlConfig}
                    value={value.html}
                    onChange={json => {
                        onChange({ ...value, html: json })
                    }}
                    onEditorBlur={() => {
                        const html = parseJsonExpression(value.html)
                        onChange({ ...value, targetHtml: html })
                    }}
                    styles={{
                        editorContent: {
                            backgroundColor: 'var(--color-gray-50)',
                            borderRadius: 6,
                            maxHeight: '600px',
                            overflowY: 'auto'
                        }
                    }}
                />
            </SCxContainer>
        </SCxListItemBaseInfo>
    )
}
